import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { visibleNavGroups } from '../utils/navFilter';
import { tokens } from '../theme';
import api from '../services/api';
import ErrorBoundary from './ErrorBoundary';
import NotificationCenter from './NotificationCenter';
import AIAssistant from './AIAssistant';
import AIAssistantWidget from './AIAssistantWidget';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
  Box, AppBar, Toolbar, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Button, Typography, Avatar, Menu, MenuItem, Badge, Chip, Divider,
  Tooltip, useMediaQuery, BottomNavigation, BottomNavigationAction,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import MapIcon from '@mui/icons-material/Map';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PeopleIcon from '@mui/icons-material/People';
import GavelIcon from '@mui/icons-material/Gavel';
import FolderIcon from '@mui/icons-material/Folder';
import ShieldIcon from '@mui/icons-material/Shield';
import EmailIcon from '@mui/icons-material/Email';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import PersonIcon  from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import QuizIcon from '@mui/icons-material/Quiz';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import StorageIcon from '@mui/icons-material/Storage';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockIcon from '@mui/icons-material/Lock';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PublicIcon from '@mui/icons-material/Public';
import LayersIcon from '@mui/icons-material/Layers';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EventIcon from '@mui/icons-material/Event';
import VerifiedUserIcon          from '@mui/icons-material/VerifiedUser';
import AssignmentTurnedInIcon    from '@mui/icons-material/AssignmentTurnedIn';
import PolicyIcon                from '@mui/icons-material/Policy';
import UploadFileIcon            from '@mui/icons-material/UploadFile';
import TaskAltIcon               from '@mui/icons-material/TaskAlt';
import ManageAccountsIcon       from '@mui/icons-material/ManageAccounts';
import VisibilityIcon            from '@mui/icons-material/Visibility';
import FolderSpecialIcon         from '@mui/icons-material/FolderSpecial';
import SupervisedUserCircleIcon  from '@mui/icons-material/SupervisedUserCircle';
import CheckCircleIcon           from '@mui/icons-material/CheckCircle';
import SecurityIcon              from '@mui/icons-material/Security';
import AccountTreeIcon           from '@mui/icons-material/AccountTree';
import ChatIcon                  from '@mui/icons-material/Chat';
import ChecklistIcon             from '@mui/icons-material/Checklist';
import SpeedIcon from '@mui/icons-material/Speed';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

const TOPBAR_H     = 56;
const SIDEBAR_W    = 240;
const BOTTOM_NAV_H = 56;
const NAVY         = '#0A2342';

const BROKER_BOTTOM_NAV = [
  { label: 'Dashboard',    path: '/broker-dashboard',    icon: DashboardIcon },
  { label: 'Licences',     path: '/licence-wallet',      icon: AccountBalanceWalletIcon },
  { label: 'CE',           path: '/ce-tracker',          icon: SchoolIcon },
  { label: 'Attestations', path: '/broker/attestations', icon: AssignmentTurnedInIcon },
  { label: 'Profile',      path: '/broker-profile',      icon: PersonIcon },
];

// â”€â”€ Nav config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

// Icons and badge keys for registry-driven nav (UI concerns stay in Layout).
// Maps registry navSection â†’ sidebar group display label.
// Enforces sidebar group order regardless of where items appear in ROUTE_REGISTRY.
/**
 * Builds PB sidebar nav groups from ROUTE_REGISTRY.
 * Only includes entries where showInSidebar is true and the user's role is
 * permitted (allowedRoles null = any role, otherwise role must be in list).
 * Injects the Governance dynamic link into the PROFILE & ADMIN group.
 */
// ── PB nav (5 sections) ─────────────────────────────────────────────────────
// Platform Admin section is only rendered for principal_broker, not deputy_pb.

const PB_NAV_MY_WORK = {
  group: 'MY WORK',
  items: [
    { icon: SpeedIcon,              label: 'PB Dashboard',      path: '/pb-dashboard' },
    { icon: GroupsIcon,             label: 'Broker Management', path: '/pb-broker-management' },
    { icon: BusinessIcon,           label: 'My Firms',          path: '/my-firms' },
    { icon: EmailIcon,              label: 'Communications',    path: '/communications' },
    { icon: MoveToInboxIcon,        label: 'Inbound for review', path: '/inbound-review' },
    { icon: VerifiedIcon,           label: 'My Licences',       path: '/licensing' },
    { icon: SchoolIcon,             label: 'CE Tracking',       path: '/ce-tracker' },
  ],
};

const PB_NAV_COMPLIANCE = {
  group: 'COMPLIANCE',
  items: [
    { icon: CalendarMonthIcon,   label: 'Filing Deadlines',         path: '/deadlines' },
    { icon: GavelIcon,           label: 'Jurisdiction Statements',  path: '/jurisdiction-statements' },
    { icon: FolderSpecialIcon,   label: 'Audit Binder',             path: '/audit-binder' },
    { icon: CalendarMonthIcon,   label: 'Compliance Calendar',      path: '/compliance-calendar' },
    { icon: AccountBalanceWalletIcon, label: 'Licence Wallet',      path: '/licence-wallet' },
    { icon: AccountBalanceWalletIcon, label: 'Import licence chart', path: '/licence-chart-import' },
    { icon: CardGiftcardIcon,    label: 'Hospitality Register',     path: '/hospitality-register' },
    { icon: EventIcon,           label: 'Conferences',              path: '/conferences' },
  ],
};

const PB_NAV_PLATFORM_ADMIN = {
  group: 'PLATFORM ADMIN',
  items: [
    { icon: AccountTreeIcon,         label: 'All Firms',             path: '/network-dashboard' },
    // Super admin only (batch H, H1): it lists every CAMGA firm in the tenant.
    { icon: GroupsIcon,              label: 'Onboarding Pipeline',   path: '/camga-pipeline', superAdminOnly: true },
    { icon: PolicyIcon,              label: 'Carrier Centre',        path: '/carrier-audits', badgeKey: 'carrierAudits' },
    { icon: LibraryBooksIcon,        label: 'Regulatory Reference',  path: '/regulatory-reference' },
  ],
};

const PB_NAV_TOOLS = {
  group: 'TOOLS',
  items: [
    { icon: MapIcon,            label: 'Regulatory Map',          path: '/regulatory-map' },
    { icon: TravelExploreIcon,  label: 'Regulatory Intelligence', path: '/regulatory-intelligence' },
    { icon: AnnouncementIcon,   label: 'Regulatory Changes',      path: '/regulatory-changes', badgeKey: 'rc' },
    { icon: ShieldIcon,         label: 'Legal Centre',            path: '/liability-shield' },
  ],
};

const PB_NAV_SYSTEM = {
  group: 'SYSTEM',
  items: [
    { icon: NotificationsIcon,  label: 'Notifications',  path: '/notification-history' },
    { icon: ManageAccountsIcon, label: 'Modifications',  path: '/role-management' },
    { icon: PersonIcon,         label: 'Settings',       path: '/profile-setup' },
  ],
};

function getPBNav(role) {
  const groups = [PB_NAV_MY_WORK, PB_NAV_COMPLIANCE];
  if (role === 'principal_broker') groups.push(PB_NAV_PLATFORM_ADMIN);
  groups.push(PB_NAV_TOOLS, PB_NAV_SYSTEM);
  return groups;
}

const BROKER_NAV = [
  {
    group: 'MY COMPLIANCE',
    items: [
      { icon: DashboardIcon,             label: 'My Dashboard',        path: '/broker-dashboard' },
      { icon: AccountBalanceWalletIcon,  label: 'My Licences',         path: '/broker-dashboard#licences' },
      { icon: AccountBalanceWalletIcon,  label: 'Licence Wallet',      path: '/licence-wallet' },
      { icon: SchoolIcon,                label: 'My CE',               path: '/ce-tracker' },
      { icon: AssignmentTurnedInIcon,    label: 'My Attestations',     path: '/attestations' },
      { icon: FolderIcon,                label: 'My Documents',        path: '/broker-documents' },
      { icon: CalendarMonthIcon,         label: 'Deadline Calendar',   path: '/broker-deadlines' },
      { icon: CardGiftcardIcon,          label: 'Hospitality Register', path: '/hospitality-register' },
      { icon: PersonIcon,                label: 'My Profile',          path: '/broker-profile' },
    ],
  },
  {
    group: 'MY CAREER',
    items: [
      { icon: BusinessCenterIcon,        label: 'Carrier Appointments', path: '/carrier-appointments' },
      { icon: SupervisedUserCircleIcon,  label: 'My Supervision',       path: '/my-supervision' },
    ],
  },
  {
    group: 'TOOLS',
    items: [
      { icon: VerifiedUserIcon,          label: 'Readiness Check',      path: '/broker-readiness' },
      { icon: LayersIcon,                label: 'Multi-Licence CE',     path: '/multi-licence-ce' },
      { icon: UploadFileIcon,            label: 'Upload CE Certificate', path: '/broker/ce-upload' },
      { icon: UploadFileIcon,            label: 'Upload Licence',       path: '/broker/licence-upload' },
      { icon: SecurityIcon,              label: 'Upload CRC',           path: '/broker/crc-upload' },
      { icon: TaskAltIcon,               label: 'Annual Declaration',   path: '/broker/declaration' },
    ],
  },
  {
    group: 'DISCOVER',
    items: [
      { icon: LibraryBooksIcon,          label: 'CE Course Finder',    path: '/ce-courses' },
      { icon: PublicIcon,                label: 'Regulator Access',    path: '/regulator-access' },
      { icon: EventIcon,                 label: 'Industry Events',     path: '/industry-events' },
      { icon: EventIcon,                 label: 'Conferences',         path: '/conferences' },
    ],
  },
  {
    group: 'ACCOUNT',
    items: [
      { icon: NotificationsIcon,         label: 'Notifications',       path: '/notification-history' },
    ],
  },
];

const MGA_NAV = [
  {
    group: 'MY FIRM',
    items: [
      { icon: DashboardIcon,    label: 'Dashboard',         path: '/mga-dashboard' },
    ],
  },
  {
    group: 'COMPLIANCE',
    items: [
      { icon: FolderSpecialIcon,      label: 'Compliance Binder', path: '/audit-binder' },
      { icon: AssignmentTurnedInIcon, label: 'Attestations',      path: '/attestations' },
      { icon: DescriptionIcon,        label: 'MGA Fact Finder',   path: '/mga-fact-finder' },
      { icon: GavelIcon,              label: 'Governance Master', key: 'governance' },
      { icon: QuizIcon,               label: 'Compliance Q&A',    path: '/compliance-questions' },
      { icon: CheckCircleIcon,        label: 'Self-Assessment',   path: '/self-assessment' },
      { icon: AccountBalanceWalletIcon, label: 'Licence Wallet',  path: '/licence-wallet' },
      { icon: CardGiftcardIcon,       label: 'Hospitality Register', path: '/hospitality-register' },
    ],
  },
  {
    group: 'OPERATIONS',
    items: [
      { icon: SecurityIcon,     label: 'E&O & Insurance',     path: '/eo-tracking' },
      { icon: AccountTreeIcon,  label: 'Binding Authorities', path: '/carrier-portal' },
      { icon: FolderIcon,       label: 'Documents',           path: '/documents' },
      { icon: UploadFileIcon,   label: 'Document Ingestion',  key: 'firm-documents' },
      { icon: ChatIcon,         label: 'Communications',      path: '/communications' },
      { icon: EventIcon,        label: 'Conferences',         path: '/conferences' },
    ],
  },
];

const CPA_NAV = [
  {
    group: 'FINANCIAL COMPLIANCE',
    items: [
      { icon: DashboardIcon,      label: 'Financial Overview', path: '/cpa-dashboard' },
      { icon: VerifiedIcon,       label: 'Trust Position Report', path: '/form1' },
      { icon: FolderIcon,         label: 'Documents',          path: '/document-repository' },
      { icon: EventIcon,          label: 'Conferences',        path: '/conferences' },
    ],
  },
];

const CARRIER_NAV = [
  {
    group: 'COMPLIANCE',
    items: [
      { icon: DashboardIcon,     label: 'Overview',            path: '/dashboard' },
      { icon: AnnouncementIcon,  label: 'Regulatory Changes',  path: '/regulatory-changes', badgeKey: 'rc' },
      { icon: TravelExploreIcon, label: 'Reg Intelligence',    path: '/regulatory-intelligence' },
    ],
  },
  {
    group: 'OPERATIONS',
    items: [
      { icon: VerifiedIcon,      label: 'Carrier Portal',      path: '/carrier-portal' },
      { icon: ChecklistIcon,     label: 'Audit Centre',        path: '/audit-centre' },
      { icon: UploadFileIcon,    label: 'Document Intake',     path: '/carrier-intake' },
      { icon: FolderIcon,        label: 'Documents',           path: '/document-repository' },
      { icon: EmailIcon,         label: 'Communications',      path: '/communications' },
      { icon: EventIcon,         label: 'Conferences',         path: '/conferences' },
    ],
  },
];

const LAWYER_NAV = [
  {
    group: 'LEGAL REVIEW',
    items: [
      { icon: DashboardIcon,     label: 'Overview',           path: '/dashboard' },
      { icon: GavelIcon,         label: 'Legal Portal',       path: '/lawyer-dashboard' },
      { icon: DescriptionIcon,   label: 'Regulatory Filings', path: '/regulatory-filings' },
      { icon: GavelIcon,         label: 'Compliance Q&A',     path: '/compliance-questions' },
      { icon: AssignmentIcon,    label: 'Audit Response',     path: '/audit-response' },
      { icon: FolderIcon,        label: 'Documents',          path: '/document-repository' },
      { icon: UploadFileIcon,    label: 'Document Ingestion', key: 'firm-documents' },
      { icon: ShieldIcon,        label: 'Liability Shield',   path: '/liability-shield' },
      { icon: EventIcon,         label: 'Conferences',        path: '/conferences' },
    ],
  },
];

const ADMIN_NAV = [
  {
    group: 'PLATFORM ADMIN',
    items: [
      { icon: AdminPanelSettingsIcon, label: 'Platform Overview', path: '/super-admin' },
      { icon: BusinessCenterIcon,     label: 'All Firms',         path: '/super-admin/firms' },
      { icon: StorageIcon,            label: 'All Filings',       path: '/super-admin/filings' },
      { icon: MarkEmailUnreadIcon,    label: 'Unmatched Email',   path: '/super-admin/inbound-unmatched' },
    ],
  },
];

const FIRM_ADMIN_NAV = [
  {
    group: 'MY FIRM',
    items: [
      { icon: DashboardIcon,             label: 'My Firm Dashboard',    path: '/firm-dashboard' },
      { icon: ChecklistIcon,             label: 'Onboarding checklist', key: 'onboarding' },
      { icon: ManageAccountsIcon,        label: 'Manage Roles',         path: '/role-management' },
      { icon: PeopleIcon,                label: 'Contacts',             key: 'firm-contacts-module' },
      { icon: FactCheckIcon,             label: 'MGA Fact Finder',      path: '/mga-fact-finder' },
      { icon: EmailIcon,                 label: 'Communications',      path: '/communications' },
    ],
  },
  {
    group: 'COMPLIANCE',
    items: [
      { icon: FolderSpecialIcon,         label: 'Compliance Binder',   path: '/audit-binder' },
      { icon: AssignmentIcon,            label: 'Compliance Questions', path: '/compliance-questions' },
      { icon: GavelIcon,                 label: 'Governance Master',    key: 'governance' },
      { icon: SupervisedUserCircleIcon,  label: 'Supervision',          path: '/supervision' },
      { icon: AssignmentTurnedInIcon,    label: 'Self-Assessment',      path: '/self-assessment' },
      { icon: DescriptionIcon,           label: 'Trust Position Report', path: '/form1' },
      { icon: ChecklistIcon,             label: 'Audits',               path: '/audit-centre' },
      { icon: UploadFileIcon,            label: 'Document Ingestion',   key: 'firm-documents' },
      { icon: CardGiftcardIcon,          label: 'Hospitality Register', path: '/hospitality-register' },
      { icon: EventIcon,                 label: 'Conferences',          path: '/conferences' },
    ],
  },
  {
    group: 'ACCOUNT',
    items: [
      { icon: PersonIcon,                label: 'My Profile',           path: '/broker-profile' },
    ],
  },
];

function getNavGroups(role, mode) {
  if (mode === 'admin') return ADMIN_NAV;
  switch (role) {
    case 'principal_broker':
    case 'deputy_pb':   return getPBNav(role);
    case 'broker':      return BROKER_NAV;
    case 'mga':         return MGA_NAV;
    case 'carrier':     return CARRIER_NAV;
    case 'cpa':         return CPA_NAV;
    case 'lawyer':      return LAWYER_NAV;
    case 'firm_admin':  return FIRM_ADMIN_NAV;
    default: return [{ group: 'NAVIGATION', items: [{ icon: DashboardIcon, label: 'Overview', path: '/dashboard' }] }];
  }
}

function getInitials(fullName) {
  if (!fullName) return '?';
  return fullName.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function navLabelKey(label) {
  return `nav.${label.toLowerCase().replace(/[\s/&-]+/g, '_')}`;
}

function navGroupKey(group) {
  return `nav.group.${group.toLowerCase().replace(/[\s/&-]+/g, '_')}`;
}

function changeLanguage(lang) {
  i18n.changeLanguage(lang);
  localStorage.setItem('i18n_language', lang);
}

// â”€â”€ Layout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, logout, mode, selectMode, activeTenantId, activeTenantName, setActiveTenant,
          activeFirmId, activeFirmName, setActiveFirm,
          viewAs, stopViewAs, realUser, effectiveUser } = useContext(AuthContext);
  const isMobile = useMediaQuery('(max-width:768px)');
  const { t, i18n: i18nInstance } = useTranslation();
  const currentLang = i18nInstance.language?.startsWith('fr') ? 'fr' : 'en';

  const [drawerOpen,        setDrawerOpen]        = useState(false);
  const [userMenuAnchor,    setUserMenuAnchor]    = useState(null);
  const [badges,            setBadges]            = useState({ complaints: 0, rc: 0, carrierAudits: 0, reviewQueue: 0 });
  const [firstFirmId,       setFirstFirmId]       = useState(null);
  const [firmIdLoading,     setFirmIdLoading]     = useState(false);
  const [firmLogoUrl,       setFirmLogoUrl]       = useState(null);

  useEffect(() => {
    if (mode === 'admin') return;
    const role = effectiveUser?.role;

    if (role !== 'lawyer') {
      // Complaint cases are for the PB and Deputy PB only (2026-11-04).
      if (role === 'principal_broker' || role === 'deputy_pb') {
        api.get('/complaints/count?status=open').then(r => {
          setBadges(prev => ({ ...prev, complaints: r.data.count || 0 }));
        }).catch(() => {});
      }

      api.get('/regulatory-changes/alerts').then(r => {
        setBadges(prev => ({ ...prev, rc: Array.isArray(r.data) ? r.data.filter(a => !a.read_at).length : 0 }));
      }).catch(() => {});
    }

    if (role === 'firm_admin') {
      setFirmIdLoading(true);
      api.get('/firm-admin/dashboard').then(r => {
        if (r.data?.firm?.id) setFirstFirmId(r.data.firm.id);
        setFirmLogoUrl(r.data?.firm?.logo_url || null);
      }).catch(err => {
        console.error('[Layout] /firm-admin/dashboard failed — Onboarding checklist nav will be disabled:', err?.response?.status, err?.response?.data?.error || err?.message);
      }).finally(() => {
        setFirmIdLoading(false);
      });
    }

    if (role === 'mga') {
      api.get('/mga-dashboard/overview').then(r => {
        if (r.data?.data?.firm_id) setFirstFirmId(r.data.data.firm_id);
      }).catch(() => {});
    }

    if (role === 'lawyer') {
      api.get('/lawyer/dashboard').then(r => {
        if (r.data?.assignments?.length > 0) setFirstFirmId(r.data.assignments[0].firm_id);
      }).catch(() => {});
    }

    if (['principal_broker', 'deputy_pb'].includes(role)) {
      api.get('/firms').then(r => {
        if (Array.isArray(r.data) && r.data.length > 0) setFirstFirmId(r.data[0].id);
      }).catch(() => {});

      api.get('/carrier-audit/list?status=pending').then(r => {
        setBadges(prev => ({ ...prev, carrierAudits: (r.data.data || []).length }));
      }).catch(() => {});

      api.get('/pb/review-queue').then(r => {
        setBadges(prev => ({ ...prev, reviewQueue: r.data.pending_count || 0 }));
      }).catch(() => {});
    }
  }, [effectiveUser?.role, mode]);

  // Super-admin-only items are hidden from everyone else, and during View As (batch H, H1).
  const navGroups = visibleNavGroups(getNavGroups(effectiveUser?.role, mode), viewAs ? false : !!auth.user?.isSuperAdmin);
  // isRealSuperAdmin: the signed-in super admin, also while in View As (auth
  // is then the viewed user) -- gates the View As banner itself.
  // isSuperAdmin: false during View As, suppressing mode-switch UI from the viewed user's screens.
  const isRealSuperAdmin = realUser?.isSuperAdmin;
  const isSuperAdmin     = viewAs ? false : auth.user?.isSuperAdmin;
  const isAdminMode      = mode === 'admin';

  const handleNavClick = (item) => {
    if (item.key === 'governance') {
      if (firstFirmId) navigate(`/governance/${firstFirmId}`);
    } else if (item.key === 'onboarding') {
      if (firstFirmId) navigate(`/firm-onboarding/${firstFirmId}`);
    } else if (item.key === 'firm-documents') {
      if (firstFirmId) navigate(`/firms/${firstFirmId}/documents`);
    } else if (item.key === 'firm-contacts-module') {
      if (firstFirmId) navigate(`/firm-contacts/${firstFirmId}`);
    } else if (item.path) {
      const [itemPath, itemHash] = item.path.split('#');
      if (itemHash) {
        navigate(itemPath + '#' + itemHash);
      } else {
        navigate(item.path);
      }
    }
    if (isMobile) setDrawerOpen(false);
  };

  const isActive = (item) => {
    if (item.key === 'governance') return location.pathname.startsWith('/governance');
    if (item.key === 'onboarding') return location.pathname.startsWith('/firm-onboarding');
    if (item.key === 'firm-documents') return location.pathname.startsWith('/firms/') && location.pathname.endsWith('/documents');
    if (item.key === 'firm-contacts-module') return location.pathname.startsWith('/firm-contacts/');
    if (!item.path) return false;
    const [itemPath, itemHash] = item.path.split('#');
    const fullPath = itemHash ? item.path : null;
    if (fullPath) {
      return location.pathname === itemPath && location.hash === `#${itemHash}`;
    }
    return (
      location.pathname === item.path ||
      location.pathname.startsWith(item.path + '/')
    );
  };

  // Derive page title from pathname
  const pageTitle = (() => {
    const p = location.pathname;
    if (p === '/pb-dashboard') return 'PB Dashboard';
    if (p === '/dashboard') return 'Overview';
    if (p === '/network-dashboard') return 'All Firms';
    if (p.startsWith('/filing-vault/review')) return 'Review Upload';
    if (p === '/filing-vault') return 'Filing Vault';
    if (p === '/super-admin/firms') return 'All Firms';
    if (p === '/super-admin/filings') return 'All Filings';
    if (p === '/super-admin/inbound-unmatched') return 'Unmatched Email';
    if (p === '/super-admin') return 'Platform Overview';
    // Generic fallback from the nav
    for (const g of navGroups) {
      for (const it of g.items) {
        if (it.path && p.startsWith(it.path)) return it.label;
      }
    }
    return 'Authra';
  })();

  // â”€â”€ Sidebar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const SidebarContent = (
    <Box sx={{
      width: SIDEBAR_W,
      height: '100%',
      bgcolor: '#080F1E',
      display: 'flex',
      flexDirection: 'column',
      borderRight: 'none',
    }}>
      {/* Logo area */}
      <Box
        sx={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/dashboard')}
      >
        <img
          src="/authra_white_transparent.png"
          alt="Authra"
          style={{ height: 160, width: 'auto', display: 'block' }}
        />
      </Box>

      {/* Firm logo (firm_admin only, when logo exists) */}
      {effectiveUser?.role === 'firm_admin' && firmLogoUrl && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Box sx={{
            bgcolor: '#FFFFFF',
            borderRadius: 1.5,
            px: 1.5, py: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Box
              component="img"
              src={firmLogoUrl}
              alt="Firm logo"
              sx={{ maxHeight: 40, maxWidth: 140, objectFit: 'contain', display: 'block' }}
            />
          </Box>
        </Box>
      )}

      {/* Scrollable nav */}
      <Box sx={{ overflowY: 'auto', flex: 1, py: 1,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 },
      }}>
        {navGroups.map((group, gi) => (
          <Box key={gi} sx={{ mb: 0.5 }}>
            <Typography sx={{
              padding: '16px 20px 6px',
              fontSize: '0.6rem',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.62)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              userSelect: 'none',
            }}>
              {t(navGroupKey(group.group), group.group)}
            </Typography>

            <List disablePadding>
              {group.items.map((item) => {
                const active = isActive(item);
                const badgeCount = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
                const Icon = item.icon;
                const needsFirmId = ['onboarding', 'governance', 'firm-documents', 'firm-contacts-module'].includes(item.key);
                const firmIdPending = needsFirmId && !firstFirmId;
                const tooltipTitle = firmIdPending
                  ? (firmIdLoading ? 'Loading…' : 'Firm data unavailable')
                  : '';
                return (
                  <Tooltip key={item.label} title={tooltipTitle} placement="right" disableHoverListener={!firmIdPending}>
                  <span>
                  <ListItemButton
                    disabled={firmIdPending}
                    onClick={() => handleNavClick(item)}
                    sx={{
                      height: 40,
                      pl: '16px',
                      pr: '12px',
                      borderRadius: 0,
                      borderLeft: active
                        ? '3px solid #0A7D72'
                        : '3px solid transparent',
                      bgcolor: active ? 'rgba(15,155,142,0.08)' : 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderLeft: active
                          ? '3px solid #0A7D72'
                          : '3px solid rgba(15,155,142,0.4)',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.35,
                      },
                      transition: 'background-color 0.15s, border-left-color 0.15s',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Badge
                        badgeContent={badgeCount || null}
                        color="error"
                        sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}
                      >
                        <Icon sx={{
                          fontSize: 18,
                          color: active ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
                          transition: 'color 0.15s',
                        }} />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={t(navLabelKey(item.label), item.label)}
                      primaryTypographyProps={{
                        fontSize: '13.5px',
                        fontWeight: active ? 600 : 500,
                        color: active ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                  </span>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Bottom — user profile */}
      <Box sx={{
        borderTop: '1px solid rgba(255,255,255,0.07)',
        padding: '12px 16px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      }}>
        <Avatar sx={{
          width: 32, height: 32,
          bgcolor: '#0A7D72',
          fontSize: 12,
          fontWeight: 600,
        }}>
          {getInitials(auth.user?.full_name)}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, color: '#FFFFFF', lineHeight: 1.3 }}>
              {auth.user?.full_name || '—'}
            </Typography>
            {effectiveUser?.role === 'deputy_pb' && (
              <Chip
                label="DPB"
                size="small"
                sx={{
                  bgcolor: '#B7791F',
                  color: '#FEFCBF',
                  fontWeight: 700,
                  fontSize: 9,
                  height: 16,
                  flexShrink: 0,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
          <Typography noWrap sx={{ fontSize: 11, color: viewAs ? '#F6E05E' : 'rgba(255,255,255,0.72)', lineHeight: 1.3, textTransform: 'capitalize' }}>
            {effectiveUser?.role === 'deputy_pb'
              ? 'Deputy Principal Broker'
              : effectiveUser?.role?.replace(/_/g, ' ') || ''}
          </Typography>
        </Box>
      </Box>

      {/* Bottom — mode switcher */}
      {isSuperAdmin && mode && (
        <Box sx={{ px: 1.5, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          {isAdminMode ? (
            <Button
              fullWidth
              size="small"
              startIcon={<BusinessCenterIcon sx={{ fontSize: 15 }} />}
              onClick={() => { selectMode('pb'); navigate('/dashboard'); if (isMobile) setDrawerOpen(false); }}
              sx={{
                color: tokens.sidebarText,
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 1.5,
                fontSize: 12,
                py: 1,
                justifyContent: 'flex-start',
                '&:hover': { bgcolor: tokens.sidebarHover, borderColor: 'rgba(255,255,255,0.2)', color: '#fff' },
              }}
            >
              Switch to My Firms
            </Button>
          ) : (
            <Button
              fullWidth
              size="small"
              startIcon={<AdminPanelSettingsIcon sx={{ fontSize: 15 }} />}
              onClick={() => { selectMode('admin'); navigate('/super-admin'); if (isMobile) setDrawerOpen(false); }}
              sx={{
                color: tokens.sidebarText,
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 1.5,
                fontSize: 12,
                py: 1,
                justifyContent: 'flex-start',
                '&:hover': { bgcolor: '#3D1A1A', borderColor: 'rgba(229,62,62,0.4)', color: '#FEB2B2' },
              }}
            >
              Switch to Admin Mode
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* â”€â”€ Topbar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: '#FFFFFF',
          borderBottom: `1px solid ${tokens.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          height: TOPBAR_H,
        }}
      >
        <Toolbar sx={{ height: TOPBAR_H, minHeight: `${TOPBAR_H}px !important`, px: { xs: 2, md: 2.5 } }}>
          {isMobile && (
            <IconButton edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 1, color: tokens.textSecondary }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* Page title */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary, lineHeight: 1.2 }}>
              {pageTitle}
            </Typography>
            {/* Firm context label â€” shown whenever a firm is selected, regardless of role */}
            {activeFirmId && !isAdminMode ? (
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: NAVY, lineHeight: 1 }}>
                Viewing: {activeFirmName || `Firm ${activeFirmId}`}
              </Typography>
            ) : isSuperAdmin && mode ? (
              <Typography sx={{ fontSize: 11, color: tokens.textMuted, lineHeight: 1 }}>
                {isAdminMode ? 'Platform Administration' : 'Principal Broker Workspace'}
              </Typography>
            ) : null}
          </Box>

          {/* Mode chip */}
          {isSuperAdmin && mode && (
            <Box sx={{ mr: 1.5 }}>
              {isAdminMode ? (
                <Chip
                  icon={<LockIcon sx={{ fontSize: '13px !important', color: `${tokens.errorRed} !important` }} />}
                  label="ADMIN MODE"
                  size="small"
                  sx={{
                    bgcolor: tokens.errorLight,
                    color: tokens.errorRed,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: '0.06em',
                    height: 22,
                    border: `1px solid rgba(197,48,48,0.2)`,
                    '& .MuiChip-icon': { color: tokens.errorRed },
                  }}
                />
              ) : (
                <Chip
                  icon={<BusinessCenterIcon sx={{ fontSize: '13px !important' }} />}
                  label="MY FIRMS"
                  size="small"
                  sx={{
                    bgcolor: '#FEFCBF',
                    color: '#744210',
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: '0.06em',
                    height: 22,
                    border: '1px solid rgba(116,66,16,0.15)',
                  }}
                />
              )}
            </Box>
          )}

          {/* Mode switch button */}
          {isSuperAdmin && mode && (
            <Tooltip title={isAdminMode ? 'Switch to My Firms' : 'Switch to Admin Mode'}>
              <Button
                size="small"
                startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  const next = isAdminMode ? 'pb' : 'admin';
                  selectMode(next);
                  navigate(next === 'admin' ? '/super-admin' : '/dashboard');
                }}
                sx={{
                  color: tokens.textSecondary,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: 1.5,
                  fontSize: 12,
                  fontWeight: 500,
                  mr: 1.5,
                  py: 0.5,
                  px: 1.5,
                  '&:hover': {
                    bgcolor: isAdminMode ? tokens.successLight : tokens.errorLight,
                    borderColor: isAdminMode ? tokens.successGreen : tokens.errorRed,
                    color: isAdminMode ? tokens.successGreen : tokens.errorRed,
                  },
                }}
              >
                {isAdminMode ? 'My Firms' : 'Admin'}
              </Button>
            </Tooltip>
          )}

          {/* Language switcher */}
          <Box sx={{ display: 'flex', mr: 1, border: `1px solid ${tokens.border}`, borderRadius: 1.5, overflow: 'hidden' }}>
            <Button
              size="small"
              onClick={() => changeLanguage('en')}
              sx={{
                minWidth: 36,
                px: 1,
                py: 0.5,
                fontSize: 12,
                fontWeight: currentLang === 'en' ? 700 : 400,
                color: currentLang === 'en' ? tokens.brandBlue : tokens.textSecondary,
                bgcolor: currentLang === 'en' ? 'rgba(10,35,66,0.06)' : 'transparent',
                borderRadius: 0,
                borderRight: `1px solid ${tokens.border}`,
                '&:hover': { bgcolor: 'rgba(10,35,66,0.08)' },
              }}
            >
              EN
            </Button>
            <Button
              size="small"
              onClick={() => changeLanguage('fr')}
              sx={{
                minWidth: 36,
                px: 1,
                py: 0.5,
                fontSize: 12,
                fontWeight: currentLang === 'fr' ? 700 : 400,
                color: currentLang === 'fr' ? tokens.brandBlue : tokens.textSecondary,
                bgcolor: currentLang === 'fr' ? 'rgba(10,35,66,0.06)' : 'transparent',
                borderRadius: 0,
                '&:hover': { bgcolor: 'rgba(10,35,66,0.08)' },
              }}
            >
              FR
            </Button>
          </Box>

          {/* Notification centre bell */}
          {!isAdminMode && <NotificationCenter />}

          {/* Welcome greeting */}
          {!isAdminMode && auth.user?.full_name && (
            <Typography sx={{ fontSize: 13, color: tokens.textSecondary, mr: 1, display: { xs: 'none', sm: 'block' } }}>
              {'Welcome, ' + auth.user.full_name.split(' ')[0]}
            </Typography>
          )}

          {/* Avatar */}
          <Tooltip title={auth.user?.full_name || ''}>
            <Avatar
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              sx={{
                width: 32, height: 32,
                bgcolor: isAdminMode ? tokens.errorRed : tokens.brandBlue,
                color: '#fff',
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                '&:hover': { opacity: 0.88 },
                transition: 'opacity 0.15s',
              }}
            >
              {getInitials(auth.user?.full_name)}
            </Avatar>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* â”€â”€ User dropdown â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 2, sx: { mt: 0.5, minWidth: 210, borderRadius: 1.5 } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600} color="text.primary">{auth.user?.full_name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {effectiveUser?.role?.replace(/_/g, ' ')}
            {isSuperAdmin && mode && (
              <Box component="span" sx={{ ml: 1, color: isAdminMode ? tokens.errorRed : tokens.brandBlue, fontWeight: 600 }}>
                Â· {isAdminMode ? 'Admin Mode' : 'My Firms'}
              </Box>
            )}
          </Typography>
        </Box>
        <Divider />
        {isSuperAdmin && mode && (
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              const next = isAdminMode ? 'pb' : 'admin';
              selectMode(next);
              navigate(next === 'admin' ? '/super-admin' : '/dashboard');
            }}
            sx={{ fontSize: 13, color: isAdminMode ? 'text.primary' : tokens.errorRed, fontWeight: 500, mx: 0.5, borderRadius: 1 }}
          >
            <SwapHorizIcon sx={{ fontSize: 16, mr: 1 }} />
            {isAdminMode ? 'Switch to My Firms' : 'Switch to Admin Mode'}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { setUserMenuAnchor(null); navigate('/profile-setup'); }}
          sx={{ fontSize: 13, fontWeight: 500, mx: 0.5, borderRadius: 1 }}
        >
          <PersonIcon sx={{ fontSize: 16, mr: 1 }} />
          {t('actions.profile_security')}
        </MenuItem>
        <MenuItem
          onClick={() => { setUserMenuAnchor(null); logout(); navigate('/login'); }}
          sx={{ color: 'error.main', fontWeight: 500, fontSize: 13, mx: 0.5, borderRadius: 1 }}
        >
          {t('actions.sign_out')}
        </MenuItem>
      </Menu>

      {/* â”€â”€ Body â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Box sx={{ display: 'flex', flex: 1, mt: `${TOPBAR_H}px` }}>

        <ErrorBoundary>
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_W, border: 'none' } }}
            >
              {SidebarContent}
            </Drawer>
          ) : (
            <Box
              component="nav"
              sx={{
                width: SIDEBAR_W,
                flexShrink: 0,
                position: 'fixed',
                top: TOPBAR_H,
                left: 0,
                height: `calc(100vh - ${TOPBAR_H}px)`,
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 0 },
              }}
            >
              {SidebarContent}
            </Box>
          )}
        </ErrorBoundary>

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            ml: isMobile ? 0 : `${SIDEBAR_W}px`,
            bgcolor: 'background.default',
            minHeight: `calc(100vh - ${TOPBAR_H}px)`,
            overflowY: 'auto',
            pb: (isMobile && effectiveUser?.role === 'broker') ? `${BOTTOM_NAV_H}px` : 0,
          }}
        >
          {/* View As banner (batch G, G2): the super admin sees the app as this user, read-only */}
          {isRealSuperAdmin && viewAs && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1,
              bgcolor: '#744210',
              color: '#FEFCBF',
              borderBottom: '2px solid #B7791F',
              flexShrink: 0,
            }}>
              <VisibilityIcon sx={{ fontSize: 16, color: '#F6E05E' }} />
              <Typography variant="caption" sx={{ flex: 1, color: '#FEF9C3', fontSize: 12 }}>
                Viewing as{' '}
                <Box component="span" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                  {viewAs.user.full_name || viewAs.user.email}
                </Box>
                {' ('}{(viewAs.user.role || '').replace(/_/g, ' ')}{'), read-only. Changes are blocked.'}
              </Typography>
              <Button
                size="small"
                onClick={async () => { await stopViewAs(); navigate('/super-admin'); }}
                sx={{
                  color: '#F6E05E',
                  border: '1px solid rgba(246,224,94,0.4)',
                  borderRadius: 1.5,
                  fontSize: 11,
                  py: 0.25,
                  px: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'rgba(246,224,94,0.12)', borderColor: '#F6E05E' },
                }}
              >
                Exit
              </Button>
            </Box>
          )}

          {/* Workspace / brokerage entry banner — suppressed during View As */}
          {isRealSuperAdmin && !viewAs && !isAdminMode && (activeTenantId || activeFirmId) && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1,
              bgcolor: NAVY,
              color: '#fff',
              borderBottom: '2px solid #1a3a5c',
              flexShrink: 0,
            }}>
              <AdminPanelSettingsIcon sx={{ fontSize: 16, color: '#90CDF4' }} />
              <Typography variant="caption" sx={{ flex: 1, color: '#CBD5E0', fontSize: 12 }}>
                {activeFirmId ? 'Viewing brokerage:' : 'Viewing workspace:'}{' '}
                <Box component="span" sx={{ color: '#fff', fontWeight: 700 }}>
                  {activeFirmId
                    ? (activeFirmName || `Firm ${activeFirmId}`)
                    : (activeTenantName || `Tenant ${activeTenantId}`)}
                </Box>
              </Typography>
              <Button
                size="small"
                startIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setActiveFirm(null, null);
                  setActiveTenant(null, null);
                  selectMode('admin');
                  navigate('/super-admin/firms');
                }}
                sx={{
                  color: '#90CDF4',
                  border: '1px solid rgba(144,205,244,0.3)',
                  borderRadius: 1.5,
                  fontSize: 11,
                  py: 0.25,
                  px: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'rgba(144,205,244,0.1)', borderColor: '#90CDF4' },
                }}
              >
                Return to Admin
              </Button>
            </Box>
          )}

          {/* Guard: super admin in PB mode with no firm or workspace selected */}
          {isSuperAdmin && !isAdminMode && !activeTenantId && !activeFirmId && !auth.user?.tenantId && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2.5, py: 0.875,
              bgcolor: '#FFF8E1', borderBottom: '1px solid #FFD54F', flexShrink: 0,
            }}>
              <AdminPanelSettingsIcon sx={{ fontSize: 15, color: '#B45309' }} />
              <Typography variant="caption" sx={{ flex: 1, color: '#7B3800', fontSize: 12 }}>
                No brokerage selected â€” select a brokerage from{' '}
                <Box
                  component="span"
                  onClick={() => { selectMode('admin'); navigate('/super-admin/firms'); }}
                  sx={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  All Firms
                </Box>
                {' '}to scope data correctly.
              </Typography>
            </Box>
          )}

          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>

          {/* Footer — Privacy Notice / Terms of Use links, present on every logged-in screen */}
          <Box
            component="footer"
            sx={{
              display: 'flex', justifyContent: 'center', gap: 3,
              py: 2, px: 2, borderTop: `1px solid ${tokens.border}`,
            }}
          >
            <Typography
              variant="caption"
              onClick={() => navigate('/privacy')}
              sx={{ color: tokens.textMuted, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Privacy Notice
            </Typography>
            <Typography
              variant="caption"
              onClick={() => navigate('/terms')}
              sx={{ color: tokens.textMuted, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Terms of Use
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* â”€â”€ Broker Bottom Navigation (mobile only) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {isMobile && effectiveUser?.role === 'broker' && (
        <BottomNavigation
          value={(() => {
            const idx = BROKER_BOTTOM_NAV.findIndex(item =>
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/')
            );
            return idx === -1 ? false : idx;
          })()}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.drawer + 2,
            borderTop: `1px solid ${tokens.border}`,
            bgcolor: '#FFFFFF',
            height: BOTTOM_NAV_H,
          }}
        >
          {BROKER_BOTTOM_NAV.map((item, idx) => {
            const Icon = item.icon;
            return (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                value={idx}
                icon={<Icon sx={{ fontSize: 22 }} />}
                onClick={() => navigate(item.path)}
                sx={{ minWidth: 'auto', px: 0, '& .MuiBottomNavigationAction-label': { fontSize: 11 } }}
              />
            );
          })}
        </BottomNavigation>
      )}

      {effectiveUser?.role === 'broker'
        ? <AIAssistantWidget />
        : <AIAssistant />
      }
    </Box>
  );
}

